import axios, { AxiosResponse } from "axios";
import { authApiV2 } from "../../axiosWrapper";
import { trackEvent, trackExceptionWithContext } from "../../services/application-insights/appInsights";
import { AppInsightsActions, AppInsightsComponents, AppInsightsErrorTypes } from "../../services/application-insights/appInsightsConstants";

export const getSubscribedMsiVesselsApi = async (sectorIds: number[]): Promise<number[] | undefined> => {
    const endpoint = `/subscribed-msi-vessels`;
    const apiCallId = `vessels-api-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    try {
        trackEvent(AppInsightsActions.SUBSCRIPTION_DATA_FETCH_ATTEMPT, {
            component: AppInsightsComponents.SUBSCRIPTION_API,
            action: 'get-subscribed-vessels-api-attempt',
            endpoint,
            sectorCount: sectorIds?.length || 0,
            sectorIds: sectorIds?.join(',') || '',
            apiCallId
        });

        const params = new URLSearchParams();
        if (sectorIds) {
            sectorIds.forEach(id => params.append('sectorIds', id.toString()));
        }

        const response: AxiosResponse<any> = await authApiV2.get(endpoint, { params, withCredentials: true });
        
        trackEvent(AppInsightsActions.SUBSCRIPTION_DATA_FETCH_SUCCESS, {
            component: AppInsightsComponents.SUBSCRIPTION_API,
            action: 'get-subscribed-vessels-api-success',
            vesselCount: response.data?.length || 0,
            sectorCount: sectorIds?.length || 0,
            apiCallId
        });

        return response.data;
    }
    catch (error: any) {
        trackExceptionWithContext(error, {
            component: AppInsightsComponents.SUBSCRIPTION_API,
            action: 'get-subscribed-vessels-api-error',
            additionalData: {
                errorType: AppInsightsErrorTypes.SUBSCRIPTION_ERROR,
                endpoint,
                sectorIds: sectorIds?.join(',') || '',
                statusCode: error.response?.status,
                errorMessage: error.message,
                apiCallId
            }
        });

        handleAxiosError(error, 'Failed to get vessels by subscribed sector');
        throw error;
    }
};

export const getSubscribedMsiVesselImageDataApi = async (sectorCode: string, imoNumber: number): Promise<string | undefined> => {
    const endpoint = `/vessel-image`;
    const apiCallId = `image-api-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    try {
        // Vessel image fetch is tracked at saga layer (appSaga.ts) for business logic context
        const response: AxiosResponse<any> = await authApiV2.get(endpoint, { params: { sectorCode, imoNumber } });
        
        return response.data;
    }
    catch (error: any) {
        trackExceptionWithContext(error, {
            component: AppInsightsComponents.SUBSCRIPTION_API,
            action: 'get-vessel-image-api-error',
            additionalData: {
                errorType: AppInsightsErrorTypes.VESSEL_DATA_ERROR,
                endpoint,
                sectorCode,
                imoNumber: imoNumber.toString(),
                statusCode: error.response?.status,
                errorMessage: error.message,
                apiCallId
            }
        });

        handleAxiosError(error, 'Failed to get vessel image data by IMO number');
        throw error;
    }
}

export const handleAxiosError = (error: unknown, defaultMessage: string) => {
    if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.message || error.response?.statusText || error.message;
        
        trackExceptionWithContext(error, {
            component: AppInsightsComponents.SUBSCRIPTION_API,
            action: 'axios-error-handler',
            additionalData: {
                errorType: AppInsightsErrorTypes.API_ERROR,
                defaultMessage,
                errorMessage,
                statusCode: error.response?.status,
                statusText: error.response?.statusText,
                isAxiosError: true
            }
        });

        throw new Error(errorMessage || defaultMessage || 'An Axios error occurred');
    }
    
    trackExceptionWithContext(error as Error, {
        component: AppInsightsComponents.SUBSCRIPTION_API,
        action: 'non-axios-error-handler',
        additionalData: {
            errorType: AppInsightsErrorTypes.UNEXPECTED_ERROR,
            defaultMessage,
            isAxiosError: false
        }
    });

    throw new Error(defaultMessage);
};

